<template>
  <div
    class="comment-wrapper"
    :class="{
      'comment-wrapper_slim': innerPagging
    }"
  >
    <BasicCheckbox
      v-if="showHistoryToggle && !isVisibleCommentWrapper"
      label="Show History"
      :value="true"
      :modelValue="filter.includeAudit"
      :lineThrough="false"
      class="comment-wrapper__checkbox"
      checkType="switch"
      @update:modelValue="onCheckboxUpdate"
    />

    <div
      v-else
      class="empty-block"
    >
      &nbsp;
    </div>

    <div
      v-if="!isVisibleCommentWrapper"
      class="comment-wrapper__list"
    >
      <ScrollToButton
        v-if="scrollToBottomButtonConfig.isVisible"
        :counter="newCommentsCount"
        class="comment-wrapper__scroll-button"
        @onClick="onScrollButtonClick"
      />
      <template v-if="isContentExist">
        <transition-group
          v-scroll="handleScroll"
          ref="commentsListRef"
          :name="!wait.is(getCommentsWaitKey) ? 'fade-comment' : ''"
          tag="div"
          class="comment-wrapper__in"
          :class="{ 'is-hidden': isVisibleCommentWrapper }"
          @scroll="onCommentsScroll"
        >
          <div
            v-for="(item, index) in comments"
            v-show="!item.isUnsupportedIdeaAudit"
            :key="getCommentRenderKey(item.id)"
            class="comment-element"
          >
            <BaseHeading
              v-if="headingIndexes.find(x => x.index === index)"
              level="2"
              color="monochrome-06"
              className="comment__day-label"
            >
              {{ headingIndexes.find(x => x.index === index)?.label }}
            </BaseHeading>
            <CommentItem
              :id="item.id"
              :comment="getEditableComment(item)"
              :owner="user.id"
              :progress="progress[item.id] ?? {}"
              :isReadonly="isReadonly"
              :editComment="Boolean(item.id && editCommentId === item.id)"
              :pendingEditHtml="item.id === editCommentId ? pendingEditHtml : ''"
              :pendingEditJson="item.id === editCommentId ? pendingEditJson : ''"
              :isPending="item.isPending"
              :comments="comments"
              :projectScoped="projectScoped"
              :showDemoVideoTimestampEditor="!!demoVideoTimestampConfig"
              :demoVideoVersionMap="resolvedDemoVideoVersionMap"
              :referencesEnabled="referencesEnabled"
              @commentMenu="commentMenu"
              @enter="enter"
              @deleteFile="openDeleteAttachmentModal"
              @onEditComment="onEditComment"
              @onEditDemoVideoTimestamp="onEditComment"
              @onOpenEditComment="onOpenEditComment"
              @onSelectReaction="onSelectReaction"
              @replyComment="onReplyComment"
              @goToRepliedComment="goToComment"
              @expandEditor="onExpandEditor(getEditableComment(item))"
              @demoVideoTimestampSelect="
                emit('demoVideoTimestampSelect', {
                  seconds: $event,
                  commentId: item.id,
                  comment: item
                })
              "
            />
          </div>
          <ObserverComponent
            key="comments-observer"
            :options="comments"
            :last="responseComments.last"
            class="comment-element comment__observer"
            @intersect="getComments"
          />
        </transition-group>
      </template>
      <p v-else>No Comments Found</p>
    </div>
    <UploadArea
      v-show="targetId"
      ref="uploadArea"
      class="comment__tasks"
      :triggerFocus="triggerFocus"
      :targetConfig="targetConfig"
      :referenceFileTargetConfig="targetConfig"
      :savedMessage="savedMessage"
      :savedFiles="savedFiles"
      :isReadonly="isReadonly"
      :replyComment="replyComment"
      :disableFocus="!!editCommentId"
      :demoVideoTimestampConfig="demoVideoTimestampConfig"
      :referencesEnabled="referencesEnabled"
      @submit="handleComment"
      @visible="visible"
      @editorExpanded="emit('editorExpanded', $event)"
      @saveMessage="saveMessage"
      @clearReplyComment="clearReplyComment"
      @cancelEditComment="onCancelEditComment"
      @saveEditComment="onSaveEditComment"
      @editCommentFilesChange="onEditCommentFilesChange"
      @demoVideoTimestampInputChange="emit('demoVideoTimestampInputChange', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router"
import { hideAllPoppers } from "floating-vue"
import { v4 as uuidv4 } from "uuid"
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  shallowRef,
  watch
} from "vue"
import { useStore } from "vuex"

import CommentItem from "@/components/Comment/CommentItem.vue"
import ScrollToButton from "@/components/Comment/ScrollToButton.vue"
import BasicCheckbox from "@/components/Form/BasicCheckbox.vue"
import ObserverComponent from "@/components/Observer/ObserverComponent.vue"
import UploadArea from "@/components/Upload/UploadArea.vue"

import { buildDemoVideoVersionMap, useDemoVideoVersions } from "@/composables/useDemoVideo"
import { getUploadProgressByEachFile } from "@/helpers/onUploadProgress"
import {
  Comment,
  CommentContentType,
  CommentFeContext,
  CommentTarget,
  DemoVideo,
  EditorData,
  EmojiType,
  FileMetaData,
  FilterComment,
  RepliedOnComment,
  ServerResponse,
  StructuredDescription,
  UploadAttachment,
  User,
  VUFile
} from "@/models"
import { useWait } from "@/plugins/vuewait"
import { Actions, Getters } from "@/store"
import { getDaysAgo, getTodayCalendarDate } from "@/utils/dateUtil"
import { parsingServerResponseContent } from "@/utils/helpers"

import type { DemoVideoTimestampConfig } from "@/components/Upload/UploadArea.vue"
import type { DemoVideoVersionInfo } from "@/composables/useDemoVideo"
import type { ComponentPublicInstance, Directive } from "vue"

interface CommentListProps {
  targetId: string
  targetResourceId: CommentTarget
  savedMessage?: string
  isReadonly?: boolean
  filterForComments: FilterComment
  savedFiles?: VUFile[]
  innerPagging?: boolean
  projectId?: string
  /** Set false for account-scoped resources that must omit projectId from requests. */
  projectScoped?: boolean
  /** Set false when the parent resource must not expose audit history. */
  showHistoryToggle?: boolean
  feContext?: CommentFeContext
  demoVideoTimestampConfig?: DemoVideoTimestampConfig | null
  demoVideoId?: string
  demoVideoVersionMap?: Record<string, DemoVideoVersionInfo>
  scrollToCommentId?: string
  referencesEnabled?: boolean
  /** The parent owns the comments response and receives every local update. */
  commentsResponse: ServerResponse<Comment>
}

interface CommentHeaderConfig {
  index: number
  label: string
}

interface ScrollToBottomButtonConfig {
  oldOffset: number
  movingTop: boolean
  isVisible: boolean
  screenSizesVisibility: number
}

interface UploadAreaRef {
  setEditingComment: (comment: Comment) => void
  finishEditingComment: () => void
  startSubmittingComment: () => void
  finishSubmittingComment: () => void
  restoreSubmittingComment: () => void
}

interface SubmitCommentPayload {
  message: string
  text: string
  json: string
  files: VUFile[]
  repliedOnComment?: RepliedOnComment | null
  demoVideoTimestamp?: Comment["demoVideoTimestamp"]
}

interface SaveMessagePayload {
  message: string
  files: VUFile[]
}

interface SaveEditPayload {
  comment: Comment
  html: string
  text: string
  json: string
  remainingFiles?: FileMetaData[]
  demoVideoTimestamp?: Comment["demoVideoTimestamp"]
}

interface SaveEditCommentPayload {
  comment: Comment
  remainingFiles?: FileMetaData[]
}

interface CommentUpdatePayload {
  commentId: string
  field: keyof Comment
  value: unknown
}

interface DemoVideoTimestampPayload {
  seconds?: number
  commentId: string
  comment?: Comment
}

const props = withDefaults(defineProps<CommentListProps>(), {
  savedMessage: "",
  isReadonly: false,
  savedFiles: () => [],
  innerPagging: true,
  projectId: undefined,
  projectScoped: true,
  showHistoryToggle: true,
  feContext: undefined,
  demoVideoTimestampConfig: null,
  demoVideoId: "",
  demoVideoVersionMap: () => ({}),
  scrollToCommentId: "",
  referencesEnabled: true
})

const emit = defineEmits<{
  (event: "editorExpanded", value: boolean): void
  (event: "saveMessage", payload: SaveMessagePayload): void
  (event: "deleteComment", payload: FileMetaData[] | undefined): void
  (event: "showAttachment", payload: { file: FileMetaData; files: FileMetaData[] }): void
  (event: "submitFilesInComment"): void
  (event: "filterUpdate", filter: FilterComment): void
  (event: "demoVideoTimestampSelect", payload: DemoVideoTimestampPayload): void
  (event: "demoVideoTimestampInputChange", seconds: number): void
  (event: "scrollToCommentHandled", payload: DemoVideoTimestampPayload): void
  (event: "commentsResponseChange", response: ServerResponse<Comment>): void
}>()

const store = useStore()
const route = useRoute()
const router = useRouter()
const wait = useWait()

const commentsListRef = ref<ComponentPublicInstance | null>(null)
const uploadArea = ref<UploadAreaRef | null>(null)
const progress = ref<Record<string, Record<string, number>>>({})
const editComment = shallowRef<Comment | null>(null)
const scrollToBottomButtonConfig = reactive<ScrollToBottomButtonConfig>({
  oldOffset: 0,
  movingTop: true,
  isVisible: false,
  screenSizesVisibility: 1
})
const isVisibleCommentWrapper = ref(false)
const replyComment = ref<RepliedOnComment | null>(null)
const navigateToCommentId = ref<string | null>(null)
const triggerFocus = ref(false)
const renderKeyById: Record<string, string> = {}
let renderKeyCounter = 0
let commentsScrollTimer: ReturnType<typeof setTimeout> | undefined
let progressResetTimer: ReturnType<typeof setTimeout> | undefined
let navigateTimer: ReturnType<typeof setTimeout> | undefined

const scrollHandlers = new WeakMap<HTMLElement, (event: Event) => void>()
const vScroll: Directive<HTMLElement, (event: Event, element: HTMLElement) => boolean | void> = {
  mounted(element, binding) {
    const onScroll = (event: Event) => {
      if (binding.value?.(event, element)) {
        element.removeEventListener("scroll", onScroll)
      }
    }

    scrollHandlers.set(element, onScroll)
    element.addEventListener("scroll", onScroll)
  },
  unmounted(element) {
    const onScroll = scrollHandlers.get(element)
    if (onScroll) {
      element.removeEventListener("scroll", onScroll)
    }
    scrollHandlers.delete(element)
  }
}

const user = computed(() => store.getters[Getters.GET_USER_CURRENT_PROFILE] as User)
const newCommentsCount = computed(() => store.getters[Getters.NEW_COMMENTS_COUNT] as number)
const filter = computed(() => props.filterForComments)
// The demo video modal mounts a second list next to the item one, and `wait` is a single
// app-wide registry - shared keys would let one list blank out the other's content and lose
// its scroll position, so each kind of list waits under its own key.
const waitKeySuffix = computed(() => (filter.value.onlyDemo ? "-demo" : ""))
const loadingCommentsWaitKey = computed(() => `loadingComments${waitKeySuffix.value}`)
const getCommentsWaitKey = computed(() => `getComments${waitKeySuffix.value}`)
const responseComments = computed(() => {
  return props.commentsResponse
})
const comments = computed<Comment[]>(() =>
  responseComments.value.content.map(comment => Comment.create(comment))
)

const { loadDemoVideoVersions } = useDemoVideoVersions()

const shouldLoadDemoVideoVersions = computed(
  () => props.targetResourceId?.type === "ITEM" && !Object.keys(props.demoVideoVersionMap).length
)
const loadedDemoVideos = ref<DemoVideo[]>([])

async function refreshDemoVideoVersions() {
  loadedDemoVideos.value = shouldLoadDemoVideoVersions.value
    ? await loadDemoVideoVersions({ projectId: props.projectId, targetId: props.targetId })
    : []
}

watch(() => [props.targetId, shouldLoadDemoVideoVersions.value], refreshDemoVideoVersions, {
  immediate: true
})

const resolvedDemoVideoVersionMap = computed(() =>
  Object.keys(props.demoVideoVersionMap).length
    ? props.demoVideoVersionMap
    : buildDemoVideoVersionMap(loadedDemoVideos.value)
)
const editCommentId = computed(() => editComment.value?.id ?? "")
const pendingEditHtml = computed(() => editComment.value?.structuredDescription?.html ?? "")
const pendingEditJson = computed(() => editComment.value?.structuredDescription?.json ?? "")
const maxUploadSizeKb = computed<number>(() => store.getters[Getters.GET_MAX_UPLOAD_SIZE_KB])
const hasVisibleComments = computed(() =>
  comments.value.some(comment => !comment.isUnsupportedIdeaAudit)
)
const isContentExist = computed(
  () =>
    (hasVisibleComments.value || (comments.value.length > 0 && !responseComments.value.last)) &&
    !wait.is(loadingCommentsWaitKey.value)
)
const commentsFiles = computed(() =>
  comments.value.reduce((files: FileMetaData[], comment) => {
    return comment.fileMetadata?.length ? [...comment.fileMetadata, ...files] : files
  }, [])
)
const targetConfig = computed(() => {
  if (!props.targetResourceId.id || !props.targetResourceId.type) return null

  return {
    type: props.targetResourceId.type,
    targetId: props.targetResourceId.id
  }
})
const headingIndexes = computed(() => {
  let currentOffset: number | undefined
  const today = getTodayCalendarDate()

  return comments.value.reduceRight((result, comment, index) => {
    if (comment.isUnsupportedIdeaAudit) return result

    const commentOffset = getDaysAgo(comment.createdDate, today)
    if (commentOffset === currentOffset) return result

    currentOffset = commentOffset
    const label =
      commentOffset === 0 ? "Today" : commentOffset === 1 ? "Yesterday" : comment.dayAndMonth
    result.push({ index, label })
    return result
  }, [] as CommentHeaderConfig[])
})

function setNewCommentsCount(count: number) {
  return store.dispatch(Actions.NEW_COMMENTS_COUNT, count)
}

function updateOwnedResponse(
  update: (response: ServerResponse<Comment>) => ServerResponse<Comment>
) {
  const response = new ServerResponse({
    ...props.commentsResponse,
    content: props.commentsResponse.content.map(comment => Comment.clone(comment))
  })
  emit("commentsResponseChange", update(response))
}

function replaceCommentInList(payload: { commentId: string; comment: Comment }) {
  updateOwnedResponse(response => {
    response.content = response.content.map(comment =>
      comment.id === payload.commentId ? payload.comment : comment
    )
    return response
  })
}

function setCommentInList(comment: Comment) {
  updateOwnedResponse(response => {
    const index = response.content.findIndex(item => item.id === comment.id)
    if (index === -1) {
      response.content.unshift(comment)
      response.totalElements += 1
      response.numberOfElements += 1
    } else {
      response.content.splice(index, 1, comment)
    }
    return response
  })
}

/**
 * A demo video comment is an ordinary comment of the same item, it is only kept in its own
 * response so the modal does not overwrite the item comments tab. Sending one from the modal
 * therefore has to reach both lists: the tab is otherwise left without it until it is reloaded,
 * since the subscription drops the author's own comment as already handled locally.
 */
function addDemoCommentToItemList(comment: Comment) {
  if (!filter.value.onlyDemo) return
  return store.dispatch(Actions.SET_COMMENT_IN_LIST, comment)
}

function removeCommentFromList(commentId: string) {
  updateOwnedResponse(response => {
    const index = response.content.findIndex(comment => comment.id === commentId)
    if (index !== -1) {
      response.content.splice(index, 1)
      response.totalElements = Math.max(0, response.totalElements - 1)
      response.numberOfElements = Math.max(0, response.numberOfElements - 1)
    }
    return response
  })
}

async function findComments(payload: { params: FilterComment; projectScoped: boolean }) {
  const response = (await store.dispatch(Actions.GET_COMMENTS, {
    ...payload,
    onlyGet: true
  })) as ServerResponse<Comment> | false
  if (response) {
    emit(
      "commentsResponseChange",
      new ServerResponse({
        ...response,
        content: parsingServerResponseContent({
          originalContent: props.commentsResponse.content,
          serverResponseContent: response.content,
          numberOfPage: response.pageable.pageNumber
        })
      })
    )
  }
  return response
}

function setCommentReaction(payload: { commentId: string; reactionType: EmojiType }) {
  return store.dispatch(Actions.SET_COMMENT_REACTION, {
    ...payload,
    skipUpdateList: true
  }) as Promise<Comment | false>
}

function createComment(payload: {
  comment: Comment
  hideToastMessage: boolean
  projectId?: string
  projectScoped: boolean
  skipAddToList?: boolean
}) {
  return store.dispatch(Actions.POST_COMMENT, payload) as Promise<Comment | false>
}

function attachmentUpload(payload: UploadAttachment) {
  return store.dispatch(Actions.ATTACHMENT_UPLOAD, payload) as Promise<FileMetaData[]>
}

function updateCommentDescription(payload: {
  comment: Comment
  projectId?: string
  projectScoped: boolean
  skipUpdateList?: boolean
}) {
  return store.dispatch(Actions.UPDATE_COMMENT_DESCRIPTION, payload) as Promise<Comment | false>
}

function removeComment(payload: { comment: Comment; projectId?: string }) {
  return store.dispatch(Actions.DELETE_COMMENT, payload)
}

function onSetAlreadyLoadedFiles(payload: { id: string; files: FileMetaData[] }) {
  updateOwnedResponse(response => {
    const comment = response.content.find(item => item.id === payload.id)
    if (comment) comment.fileMetadata = [...(comment.fileMetadata ?? []), ...payload.files]
    return response
  })
}

function updateCommentValue(payload: CommentUpdatePayload) {
  updateOwnedResponse(response => {
    const comment = response.content.find(item => item.id === payload.commentId)
    if (comment) Object.assign(comment, { [payload.field]: payload.value })
    return response
  })
}

function getCommentsElement(): HTMLElement | null {
  return (commentsListRef.value?.$el as HTMLElement | undefined) ?? null
}

function getCommentRenderKey(commentId: string) {
  if (!renderKeyById[commentId]) {
    renderKeyCounter += 1
    renderKeyById[commentId] = `comment-render-${renderKeyCounter}`
  }
  return renderKeyById[commentId]
}

function getEditableComment(comment: Comment) {
  return editComment.value?.id === comment.id ? editComment.value : comment
}

function handleScroll() {
  hideAllPoppers()
}

async function goToComment(commentId: string) {
  if (!commentId) return

  const comment = comments.value.find(item => item.id === commentId)
  if (comment) {
    wait.start("disableUpdateComponentByRoute")
    const query = { ...route.query }
    delete query.comment
    await router.replace({ query })
    wait.end("disableUpdateComponentByRoute")
    navigateToCommentId.value = commentId
    clearTimeout(navigateTimer)
    navigateTimer = setTimeout(() => {
      scrollToComment()
      const file = comment.fileMetadata?.find(item => item.id === route.query.file)
      if (file) {
        emit("showAttachment", { file, files: commentsFiles.value })
      }
    }, 150)
    return
  }

  if (filter.value.page > responseComments.value.totalPages) return
  await getComments()
  await goToComment(commentId)
}

async function handleScrollToCommentRequest(commentId: string) {
  if (!commentId) return

  await goToComment(commentId)
  const comment = comments.value.find(item => item.id === commentId)
  emit("scrollToCommentHandled", { commentId, comment })
}

function scrollToComment() {
  if (!navigateToCommentId.value) return

  const element = getCommentsElement()
  if (!element) return

  const escapedId = CSS.escape(navigateToCommentId.value)
  const commentElement = element.querySelector<HTMLElement>(`#${escapedId}`)
  if (commentElement) {
    element.scrollTop = commentElement.offsetTop
  }
  navigateToCommentId.value = null
}

function onOpenEditComment(commentId: string) {
  if (!commentId) {
    editComment.value = null
    return
  }

  const comment = comments.value.find(item => item.id === commentId)
  if (comment) {
    editComment.value = Comment.clone(comment)
  }
}

function visible() {
  isVisibleCommentWrapper.value = !isVisibleCommentWrapper.value
}

async function onCheckboxUpdate(value: boolean) {
  filter.value.includeAudit = value
  triggerFocus.value = !triggerFocus.value
  await getFirstPage()
  onScrollButtonClick()
}

async function getComments(reset = false) {
  if (!props.targetId) return

  wait.start(getCommentsWaitKey.value)
  if (reset) {
    filter.value.page++
  }
  filter.value.projectId = props.projectId
  filter.value.targetId = props.targetId
  await findComments({ params: filter.value, projectScoped: props.projectScoped })
  filter.value.page++
  wait.end(getCommentsWaitKey.value)
}

async function getFirstPage() {
  filter.value.page = 0
  await getComments(true)
}

async function handleComment({
  message,
  text,
  json,
  files,
  repliedOnComment,
  demoVideoTimestamp
}: SubmitCommentPayload) {
  if (await store.dispatch(Actions.HAS_FILE_OVER_SIZE_LIMIT, { files })) return false

  const hasFiles = files.length > 0
  const tempId = uuidv4()
  const wasExpanded = isVisibleCommentWrapper.value
  const pendingReplyComment = replyComment.value
  const pendingFiles = hasFiles
    ? files
        .filter(file => file.size / 1024 < maxUploadSizeKb.value)
        .map(file =>
          FileMetaData.create({
            id: String(file.id),
            name: file.name,
            size: file.size,
            targetType: file.name,
            createdDate: new Date().toISOString(),
            owner: user.value,
            custom: true
          })
        )
    : []

  const comment = Comment.create({
    id: tempId,
    structuredDescription: new StructuredDescription({ json, html: message, text }),
    targetResourceId: props.targetResourceId,
    replyOnCommentId: repliedOnComment?.id,
    repliedOnComment: repliedOnComment ?? undefined,
    owner: user.value,
    fileMetadata: pendingFiles,
    feContext: props.feContext,
    demoVideoId: props.demoVideoId,
    demoVideoTimestamp: props.demoVideoTimestampConfig ? (demoVideoTimestamp ?? null) : null,
    isPending: true
  })

  setCommentInList(comment)
  uploadArea.value?.startSubmittingComment()
  isVisibleCommentWrapper.value = false
  replyComment.value = null
  await nextTick()
  onScrollButtonClick()

  if (hasFiles) {
    const attachmentIds = await onUploadFile(files)
    if (attachmentIds === false) {
      removeCommentFromList(tempId)
      isVisibleCommentWrapper.value = wasExpanded
      replyComment.value = pendingReplyComment
      uploadArea.value?.restoreSubmittingComment()
      return false
    }
    comment.attachmentIds = attachmentIds
  }

  const result = await createComment({
    comment,
    hideToastMessage: true,
    projectId: props.projectScoped ? props.projectId : undefined,
    projectScoped: props.projectScoped,
    skipAddToList: true
  })
  if (!result) {
    removeCommentFromList(tempId)
    isVisibleCommentWrapper.value = wasExpanded
    replyComment.value = pendingReplyComment
    uploadArea.value?.restoreSubmittingComment()
    return false
  }
  if (result.id && result.id !== tempId) {
    renderKeyById[result.id] = getCommentRenderKey(tempId)
    delete renderKeyById[tempId]
  }

  replaceCommentInList({ commentId: tempId, comment: result })
  addDemoCommentToItemList(result)
  isVisibleCommentWrapper.value = false
  emit("editorExpanded", false)
  uploadArea.value?.finishSubmittingComment()
  return true
}

async function onUploadFile(files: VUFile[]): Promise<string[] | false> {
  const commentId = editCommentId.value
  const filteredAttachments = files.filter(file => file.size / 1024 < maxUploadSizeKb.value)
  if (!filteredAttachments.length) return []

  if (commentId) {
    onSetAlreadyLoadedFiles({
      id: commentId,
      files: filteredAttachments.map(file =>
        FileMetaData.create({
          id: file.id.toString(),
          name: file.name,
          createdDate: file.file.lastModified.toString(),
          size: file.size,
          targetType: file.name,
          owner: User.createDefault(),
          custom: true
        })
      )
    })
  }

  const attachmentsResponse = await attachmentUpload({
    attachments: filteredAttachments.map(file => file.file),
    projectId: props.projectScoped ? props.projectId : undefined,
    projectScoped: props.projectScoped,
    onlySend: true,
    onUploadProgress: event => {
      const progressCount = getUploadProgressByEachFile(event, filteredAttachments)
      if (commentId) {
        progress.value[commentId] = progressCount
      }

      if (event.loaded === event.total) {
        clearTimeout(progressResetTimer)
        progressResetTimer = setTimeout(() => {
          progress.value = {}
        }, 1000)
      }
    }
  })

  emit("submitFilesInComment")
  return attachmentsResponse ? attachmentsResponse.map(file => file.id) : false
}

function onEditComment({ commentId, field, value }: CommentUpdatePayload) {
  if (editComment.value?.id === commentId) {
    const clone = Comment.clone(editComment.value)
    Object.assign(clone, { [field]: value })
    editComment.value = clone
    return
  }

  updateCommentValue({ commentId, field, value })
}

function updateEditCommentDescription(value: { html: string; text: string; json: string }) {
  if (!editComment.value) return

  const clone = Comment.clone(editComment.value)
  clone.structuredDescription = new StructuredDescription({
    ...clone.structuredDescription,
    ...value
  })
  editComment.value = clone
}

function getEmailTextFromHtml(html: string) {
  const editorData = new EditorData()
  editorData.setHtml(html)
  return editorData.getEmailText()
}

function prepareCommentForSave(comment: Comment) {
  const commentToSave = Comment.clone(comment)
  commentToSave.attachmentIds =
    commentToSave.fileMetadata
      ?.filter(file => !file.custom)
      .map(file => file.id)
      .filter(Boolean) ?? []
  return commentToSave
}

function getDeletedFileMetadataIds(originalComment: Comment, editedComment: Comment) {
  const remainingIds = editedComment.fileMetadata?.map(file => file.id) ?? []
  return (
    originalComment.fileMetadata
      ?.filter(file => !remainingIds.includes(file.id))
      .map(file => file.id)
      .filter(Boolean) ?? []
  )
}

async function uploadPendingFilesToComment(comment: Comment): Promise<string[] | false> {
  const newFiles = comment.fileMetadata?.filter(file => file.custom && file.pendingFile) ?? []
  if (!newFiles.length) return []

  const uploadedFiles = await attachmentUpload({
    attachments: newFiles.map(file => file.pendingFile as File),
    projectId: props.projectScoped ? props.projectId : undefined,
    projectScoped: props.projectScoped,
    onlySend: true
  })

  return uploadedFiles ? uploadedFiles.map(file => file.id) : false
}

function isStatusChangeComment(comment: Comment) {
  return comment.type === CommentContentType.STATUS
}

async function enter({ comment }: { comment: Comment }) {
  const editableComment = editComment.value?.id === comment.id ? editComment.value : comment

  if (pendingEditHtml.value && editCommentId.value === editableComment.id) {
    onEditComment({
      commentId: editableComment.id,
      field: "structuredDescription",
      value: {
        html: pendingEditHtml.value,
        json: pendingEditJson.value,
        text: getEmailTextFromHtml(pendingEditHtml.value)
      }
    })
  }

  const targetComment = comments.value.find(item => item.id === editableComment.id)
  if (!targetComment) {
    editComment.value = null
    return
  }

  const commentToSave = prepareCommentForSave(
    editComment.value?.id === editableComment.id ? editComment.value : editableComment
  )
  commentToSave.feContext = props.feContext ?? commentToSave.feContext
  if (
    !isStatusChangeComment(commentToSave) &&
    !commentToSave.structuredDescription.text?.trim() &&
    !commentToSave.fileMetadata?.length
  ) {
    deleteComment(commentToSave)
  } else {
    const originalComment = Comment.clone(targetComment)
    commentToSave.deletedFileMetadataIds = getDeletedFileMetadataIds(targetComment, commentToSave)
    setCommentInList(Comment.clone(commentToSave))

    const uploadedFileIds = await uploadPendingFilesToComment(commentToSave)
    if (uploadedFileIds === false) {
      replaceCommentInList({ commentId: originalComment.id, comment: originalComment })
      return
    }

    commentToSave.attachmentIds = [...(commentToSave.attachmentIds ?? []), ...uploadedFileIds]
    const updatedComment = await updateCommentDescription({
      comment: commentToSave,
      projectId: props.projectScoped ? props.projectId : undefined,
      projectScoped: props.projectScoped,
      skipUpdateList: true
    })
    if (!updatedComment) {
      replaceCommentInList({ commentId: originalComment.id, comment: originalComment })
      return
    }

    setCommentInList(Comment.clone(updatedComment))
    if (uploadedFileIds.length) {
      await getFirstPage()
    }
  }

  editComment.value = null
}

function commentMenu({ value, comment }: { value: string; comment: Comment }) {
  comments.value.forEach(item => {
    if (comment.id === item.id && value === "delete") {
      deleteComment(comment)
    }
  })
}

function onScrollButtonClick() {
  const element = getCommentsElement()
  if (!element) return

  element.scrollTop = 0
  scrollToBottomButtonConfig.isVisible = false
  setNewCommentsCount(0)
}

async function onSelectReaction(payload: { commentId: string; reactionType: EmojiType }) {
  const updatedComment = await setCommentReaction(payload)
  if (!updatedComment) return

  replaceCommentInList({ commentId: updatedComment.id, comment: updatedComment })
  if (filter.value.onlyDemo) {
    store.dispatch(Actions.REPLACE_COMMENT_IN_LIST, {
      commentId: updatedComment.id,
      comment: updatedComment
    })
  }
}

function onReplyComment(comment: Comment) {
  replyComment.value = new RepliedOnComment({ ...comment })
  triggerFocus.value = !triggerFocus.value
}

function clearReplyComment() {
  replyComment.value = null
}

function handleCommentsScroll() {
  const element = getCommentsElement()
  if (!element) return

  scrollToBottomButtonConfig.movingTop = element.scrollTop < scrollToBottomButtonConfig.oldOffset
  const height = element.getBoundingClientRect().height

  if (
    (scrollToBottomButtonConfig.movingTop &&
      height * scrollToBottomButtonConfig.screenSizesVisibility < Math.abs(element.scrollTop)) ||
    (element.scrollTop < -100 && newCommentsCount.value > 0)
  ) {
    scrollToBottomButtonConfig.isVisible = true
  } else if (
    !scrollToBottomButtonConfig.movingTop &&
    height * scrollToBottomButtonConfig.screenSizesVisibility > Math.abs(element.scrollTop)
  ) {
    scrollToBottomButtonConfig.isVisible = false
    setNewCommentsCount(0)
  }
  scrollToBottomButtonConfig.oldOffset = element.scrollTop
}

function onCommentsScroll() {
  clearTimeout(commentsScrollTimer)
  commentsScrollTimer = setTimeout(handleCommentsScroll, 100)
}

function openDeleteAttachmentModal({ file, commentId }: { file: FileMetaData; commentId: string }) {
  const comment =
    editComment.value?.id === commentId
      ? editComment.value
      : comments.value.find(item => item.id === commentId)
  if (!comment?.fileMetadata) return

  onEditComment({
    commentId,
    field: "fileMetadata",
    value: comment.fileMetadata.filter(item => item.id !== file.id)
  })
}

function saveMessage(payload: SaveMessagePayload) {
  nextTick(onScrollButtonClick)
  emit("saveMessage", payload)
}

async function deleteComment(comment: Comment) {
  if (comment.id === replyComment.value?.id) {
    clearReplyComment()
  }
  await removeComment({
    comment,
    projectId: props.projectId
  })
  removeCommentFromList(comment.id)
  emit("deleteComment", comment.fileMetadata)
}

function onExpandEditor(comment: Comment) {
  isVisibleCommentWrapper.value = true
  emit("editorExpanded", true)
  uploadArea.value?.setEditingComment(comment)
}

function onCancelEditComment(data?: {
  comment: Comment | null
  currentHtml: string
  currentJson?: string
}) {
  isVisibleCommentWrapper.value = false
  if (!data?.comment?.id) {
    editComment.value = null
    return
  }

  if (!editComment.value) {
    const comment = comments.value.find(item => item.id === data.comment?.id)
    editComment.value = comment ? Comment.clone(comment) : null
  }
  if (!editComment.value) return

  updateEditCommentDescription({
    html: data.currentHtml || "",
    json: data.currentJson || pendingEditJson.value,
    text: getEmailTextFromHtml(data.currentHtml || "")
  })
  if (data.comment.fileMetadata) {
    onEditComment({
      commentId: editComment.value.id,
      field: "fileMetadata",
      value: data.comment.fileMetadata
    })
  }
}

function onEditCommentFilesChange({ files }: { files: FileMetaData[] }) {
  if (!editComment.value) return

  onEditComment({
    commentId: editComment.value.id,
    field: "fileMetadata",
    value: files
  })
}

async function onSaveEditComment(data: SaveEditPayload) {
  const addedFiles = data.remainingFiles?.filter(file => file.pendingFile) ?? []
  if (await store.dispatch(Actions.HAS_FILE_OVER_SIZE_LIMIT, { files: addedFiles })) return

  onEditComment({
    field: "structuredDescription",
    value: {
      html: data.html,
      text: getEmailTextFromHtml(data.html),
      json: data.json
    },
    commentId: data.comment.id
  })
  if (data.remainingFiles && editComment.value?.id === data.comment.id) {
    onEditComment({
      field: "fileMetadata",
      value: data.remainingFiles,
      commentId: data.comment.id
    })
  }
  if (props.demoVideoTimestampConfig) {
    onEditComment({
      field: "demoVideoTimestamp",
      value: data.demoVideoTimestamp ?? null,
      commentId: data.comment.id
    })
  }

  const commentToSave = prepareCommentForSave(
    editComment.value?.id === data.comment.id ? editComment.value : data.comment
  )
  commentToSave.feContext = props.feContext ?? commentToSave.feContext
  const isSaved = await saveEditCommentAsync({ ...data, comment: commentToSave })
  if (!isSaved) return

  isVisibleCommentWrapper.value = false
  emit("editorExpanded", false)
  editComment.value = null
  uploadArea.value?.finishEditingComment()
}

async function saveEditCommentAsync(data: SaveEditCommentPayload) {
  const originalComment = comments.value.find(comment => comment.id === data.comment.id)
  const originalCommentClone = originalComment ? Comment.clone(originalComment) : null
  if (originalComment) {
    data.comment.deletedFileMetadataIds = getDeletedFileMetadataIds(originalComment, data.comment)
  }
  setCommentInList(Comment.clone(data.comment))

  const uploadedFileIds = await uploadPendingFilesToComment(data.comment)
  if (uploadedFileIds === false) {
    if (originalCommentClone) {
      replaceCommentInList({
        commentId: originalCommentClone.id,
        comment: originalCommentClone
      })
    }
    return false
  }

  data.comment.attachmentIds = [...(data.comment.attachmentIds ?? []), ...uploadedFileIds]
  const updatedComment = await updateCommentDescription({
    comment: data.comment,
    projectId: props.projectScoped ? props.projectId : undefined,
    projectScoped: props.projectScoped,
    skipUpdateList: true
  })
  if (!updatedComment) {
    if (originalCommentClone) {
      replaceCommentInList({
        commentId: originalCommentClone.id,
        comment: originalCommentClone
      })
    }
    return false
  }

  setCommentInList(Comment.clone(updatedComment))
  if (uploadedFileIds.length) {
    await getFirstPage()
  }
  return true
}

watch(newCommentsCount, count => {
  if (count <= 0) return

  const element = getCommentsElement()
  if (!element || element.scrollTop > -100) {
    setNewCommentsCount(0)
    return
  }
  scrollToBottomButtonConfig.isVisible = true
})

watch(
  () => props.scrollToCommentId,
  commentId => handleScrollToCommentRequest(commentId)
)

onMounted(async () => {
  wait.start(loadingCommentsWaitKey.value)
  filter.value.page = 1
  await getFirstPage()
  if (props.scrollToCommentId) {
    await handleScrollToCommentRequest(props.scrollToCommentId)
  } else {
    await goToComment(route.query.comment as string)
  }
  wait.end(loadingCommentsWaitKey.value)
})

onBeforeUnmount(() => {
  clearTimeout(commentsScrollTimer)
  clearTimeout(progressResetTimer)
  clearTimeout(navigateTimer)
})
</script>
